import { getProductById, listVariantsForProduct } from '../../catalog/infra-api';
import { isComboValidityOutOfWindow, unitPriceWithOptions } from '../../catalog/domain-api';
import type { CartItem } from '../store/usePosStore';
import { formatMoney } from '../../shared/format/public-api';

export type PosCartCatalogQuote = {
  productId: string;
  variantId: string | null;
  catalogUnitPrice: number | null;
  isActive: boolean;
  menuAvailable: boolean;
  isMisc?: boolean;
  comboValidFrom?: string | null;
  comboValidUntil?: string | null;
};

export type PosCartCatalogDriftKind = 'price' | 'inactive' | 'paused' | 'missing' | 'combo_expired';

export type PosCartCatalogDrift = {
  lineId: string;
  productName: string;
  kind: PosCartCatalogDriftKind;
  cartPrice: number;
  catalogPrice?: number;
};

export type ResolvePosListUnitPrice = (args: {
  productId: string;
  variantId?: string | null;
  basePrice: number;
  quantity?: number;
}) => number;

const PRICE_CENTS_TOLERANCE = 1;

export function cartCatalogQuoteKey(productId: string, variantId?: string | null): string {
  return `${productId}::${variantId ?? ''}`;
}

function pricesDiverge(cartPrice: number, catalogPrice: number): boolean {
  return Math.abs(Math.round(cartPrice * 100) - Math.round(catalogPrice * 100)) > PRICE_CENTS_TOLERANCE;
}

export function diffCartAgainstCatalog(
  cart: CartItem[],
  quotesByKey: Map<string, PosCartCatalogQuote>,
  now: Date = new Date(),
): PosCartCatalogDrift[] {
  const drifts: PosCartCatalogDrift[] = [];
  for (const line of cart) {
    // 'custom' y 'package' no se chequean contra el catálogo de productos.
    if (line.itemType !== 'product') continue;
    const key = cartCatalogQuoteKey(line.productId, line.variantId);
    const quote = quotesByKey.get(key);
    // is_misc = precio libre por línea (ej. repuesto cotizado en OT): el
    // server exime misc del pricing autoritativo, así que no hay drift de
    // precio/estado. Solo la vigencia de combo se valida igual que allá.
    if (quote?.isMisc) {
      if (isComboValidityOutOfWindow(quote, now)) {
        drifts.push({
          lineId: line.lineId,
          productName: line.name,
          kind: 'combo_expired',
          cartPrice: line.price,
        });
      }
      continue;
    }
    if (!quote || quote.catalogUnitPrice == null) {
      drifts.push({
        lineId: line.lineId,
        productName: line.name,
        kind: 'missing',
        cartPrice: line.price,
      });
      continue;
    }
    if (!quote.isActive) {
      drifts.push({
        lineId: line.lineId,
        productName: line.name,
        kind: 'inactive',
        cartPrice: line.price,
        catalogPrice: quote.catalogUnitPrice,
      });
      continue;
    }
    if (!quote.menuAvailable) {
      drifts.push({
        lineId: line.lineId,
        productName: line.name,
        kind: 'paused',
        cartPrice: line.price,
        catalogPrice: quote.catalogUnitPrice,
      });
      continue;
    }
    // Combo fuera de vigencia con el carrito ya armado (#1317): el server
    // rechaza con `combo_not_available` — hay que avisar antes del submit.
    if (isComboValidityOutOfWindow(quote, now)) {
      drifts.push({
        lineId: line.lineId,
        productName: line.name,
        kind: 'combo_expired',
        cartPrice: line.price,
      });
      continue;
    }
    if (pricesDiverge(line.price, quote.catalogUnitPrice)) {
      drifts.push({
        lineId: line.lineId,
        productName: line.name,
        kind: 'price',
        cartPrice: line.price,
        catalogPrice: quote.catalogUnitPrice,
      });
    }
  }
  return drifts;
}

function formatDriftLine(drift: PosCartCatalogDrift): string {
  if (drift.kind === 'combo_expired') {
    return `El combo ${drift.productName} quedó fuera de vigencia.`;
  }
  if (drift.kind === 'price') {
    return `${drift.productName} en el carrito sigue a ${formatMoney(drift.cartPrice)}. En Catálogo ahora está ${formatMoney(drift.catalogPrice ?? 0)}.`;
  }
  if (drift.kind === 'paused') {
    return `${drift.productName} está pausado en Catálogo.`;
  }
  if (drift.kind === 'inactive') {
    return `${drift.productName} está desactivado en Catálogo.`;
  }
  return `${drift.productName} ya no está en el catálogo.`;
}

/**
 * True cuando el drift implica un precio distinto al congelado en el carrito.
 * El server valida pricing autoritativo (`sale_price_mismatch`): el precio
 * viejo no se puede cobrar, solo actualizar la línea al valor de catálogo.
 */
export function hasCartPriceDrift(drifts: PosCartCatalogDrift[]): boolean {
  return drifts.some(
    (drift) =>
      drift.catalogPrice != null && pricesDiverge(drift.cartPrice, drift.catalogPrice),
  );
}

/**
 * Drifts que no admiten «cobrar igual» ni actualizar precio: el server
 * rechaza el submit. El cajero tiene que editar el carrito (quitar la línea).
 */
export function hasBlockingCartDrift(drifts: PosCartCatalogDrift[]): boolean {
  return drifts.some((drift) => drift.kind === 'combo_expired');
}

export function formatPosCartCatalogDriftMessage(drifts: PosCartCatalogDrift[]): string {
  const head = drifts.slice(0, 3).map(formatDriftLine);
  const extra = drifts.length > 3 ? `\nY ${drifts.length - 3} más.` : '';
  const tail = hasBlockingCartDrift(drifts)
    ? 'Quitá el combo del carrito y volvé a intentar el cobro.'
    : hasCartPriceDrift(drifts)
      ? 'Si continuás, el carrito se actualiza con el precio actual de Catálogo.'
      : 'El precio no cambió; podés cobrar igual.';
  return `${head.join('\n')}${extra}\n\n${tail}`;
}

type QuoteFetchers = {
  getProductById: typeof getProductById;
  listVariantsForProduct: typeof listVariantsForProduct;
};

const defaultFetchers: QuoteFetchers = { getProductById, listVariantsForProduct };

export async function loadPosCartCatalogQuotes(
  cart: CartItem[],
  resolveListPrice: ResolvePosListUnitPrice,
  fetchers: QuoteFetchers = defaultFetchers,
): Promise<Map<string, PosCartCatalogQuote>> {
  const productLines = cart.filter((line) => line.itemType === 'product');
  // #1301: fetch paralelo deduplicado por productId — antes era un await
  // secuencial por línea (N roundtrips bloqueando el botón Cobrar).
  const productIds = [...new Set(productLines.map((line) => line.productId))];
  const products = new Map(
    (await Promise.all(productIds.map((id) => fetchers.getProductById(id)))).map(
      (product, i) => [productIds[i], product] as const,
    ),
  );
  const variantProductIds = [
    ...new Set(productLines.filter((line) => line.variantId).map((line) => line.productId)),
  ];
  const variantsByProduct = new Map(
    (
      await Promise.all(variantProductIds.map((id) => fetchers.listVariantsForProduct(id)))
    ).map((variants, i) => [variantProductIds[i], variants] as const),
  );

  const quotes = new Map<string, PosCartCatalogQuote>();
  for (const line of productLines) {
    const key = cartCatalogQuoteKey(line.productId, line.variantId);
    const product = products.get(line.productId);
    if (!product) {
      quotes.set(key, {
        productId: line.productId,
        variantId: line.variantId ?? null,
        catalogUnitPrice: null,
        isActive: false,
        menuAvailable: false,
      });
      continue;
    }
    let base = product.price;
    if (line.variantId) {
      const variants = variantsByProduct.get(line.productId) ?? [];
      const variant = variants.find((row) => row.id === line.variantId);
      if (!variant) {
        quotes.set(key, {
          productId: line.productId,
          variantId: line.variantId,
          catalogUnitPrice: null,
          isActive: false,
          menuAvailable: product.menuAvailable !== false,
          isMisc: product.isMisc === true,
        });
        continue;
      }
      if (variant.price != null && Number.isFinite(variant.price)) {
        base = variant.price;
      }
    }
    const withOptions = unitPriceWithOptions(base, line.menuOptions);
    const catalogUnitPrice = resolveListPrice({
      productId: line.productId,
      variantId: line.variantId,
      basePrice: withOptions,
      quantity: line.quantity,
    });
    quotes.set(key, {
      productId: line.productId,
      variantId: line.variantId ?? null,
      catalogUnitPrice,
      isActive: product.isActive !== false,
      menuAvailable: product.menuAvailable !== false,
      isMisc: product.isMisc === true,
      comboValidFrom: product.comboValidFrom ?? null,
      comboValidUntil: product.comboValidUntil ?? null,
    });
  }
  return quotes;
}
